import React, { useEffect, useState, useRef, useContext } from 'react';
import { Context } from '../../../App';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MuiSnackbar from '../../common/MuiSnackbar';
import CircularProgress from '@mui/material/CircularProgress';
import SeoHelmet from '../../common/SeoHelmet';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import StroudResult from './StroudResult';
import StroudF1GraphSvg from './StroudF1GraphSvg';
import StroudF2GraphSvg from './StroudF2GraphSvg';
import PricingInfoModal from '../../modals/PricingInfoModal';
import terzaghiImage from '../../../assets/calculations/terzaghi/terzaghi.png';
import TransitionsModal from '../../modals/TransitionsModal';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const StroudPdfCaptureGraphs = React.memo(function StroudPdfCaptureGraphs({ result, f1GraphRef, f2GraphRef }) {
  if (!result?.outputs) return null;

  const piValue = Number(result?.inputs?.pi);
  const f1Value = Number(result?.outputs?.f1);
  const f2Value = Number(result?.outputs?.f2);

  return (
    <>
      <div
        ref={f1GraphRef}
        style={{
          position: 'absolute',
          left: -99999,
          top: -99999,
          width: 380,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <StroudF1GraphSvg pi={piValue} f1={f1Value} showOverlay={piValue >= 15 && piValue <= 64} />
      </div>
      <div
        ref={f2GraphRef}
        style={{
          position: 'absolute',
          left: -99999,
          top: -99999,
          width: 380,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <StroudF2GraphSvg pi={piValue} f2={f2Value} showOverlay={piValue >= 12 && piValue <= 65} />
      </div>
    </>
  );
});

const Stroud = () => {
  const { user } = useContext(Context);

  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    lastData,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  //SET STATES FOR INPUT VARIABLES
  const [name, setName] = useState('');
  const [pi, setPi] = useState('');
  const [spt, setSpt] = useState('');

  //PDF RELATED STATES
  const f1GraphRef = useRef(null);
  const f2GraphRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);

  // SET THE LOADED INPUTS
  const setInputs = (inputs) => {
    setName(inputs.name);
    setPi(inputs.pi);
    setSpt(inputs.spt);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    name,
    pi,
    spt,
  };

  // MANAGE SAVE & CALCULATE BUTTONS DISABLED/ENABLED STATES
  useEffect(() => {
    setCalculateButtonDisabled(false);
    setSaveButtonDisabled(false);

    if (
      (calculationInput.name || calculationInput.name === 0) &&
      (calculationInput.pi || calculationInput.pi === 0) &&
      (calculationInput.spt || calculationInput.spt === 0)
    ) {
      setCalculateButtonDisabled(false);
      setSaveButtonDisabled(false);
    } else {
      setCalculateButtonDisabled(true);
      setSaveButtonDisabled(true);
    }
  }, [setCalculateButtonDisabled, setSaveButtonDisabled, calculationInput.name, calculationInput.pi, calculationInput.spt]);

  // Do not render output if any input parameter except name changes
  useEffect(() => {
    setShowOutput(false);
  }, [setShowOutput, calculationInput.pi, calculationInput.spt]);

  //CLEAR INPUTS AND CLEAR OUTPUT
  const createNewCalculation = () => {
    setName('');
    setPi('');
    setSpt('');
    setShowOutput(false);
  };

  //API URLS
  const URL = {
    CALCULATE: '/api/v1/calculations/stroud',
    LOADONE: `/api/v1/calculations/stroud/loadOne/`,
    LOADALL: '/api/v1/calculations/stroud/loadAll',
    UPDATE: `/api/v1/calculations/stroud/update/`,
    SAVE: '/api/v1/calculations/stroud/save',
    CHECKUNIQUENAME: `/api/v1/calculations/stroud/checkUniqueName/`,
    DELETE: '/api/v1/calculations/stroud/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  //HANDLE PDF
  const handleExportPdf = async () => {
    if (isExporting) return;
    if (user.role === 'basic' || user.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }
    setIsExporting(true);
    try {
      // Ensure off-screen SVGs are mounted and painted before capture.
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      const [f1Graph, f2Graph] = await Promise.all([
        svgContainerToPngBase64(f1GraphRef.current, {
          scale: 2, // 1)Low resolution, 2)High Resolution
          inlineImages: true,
        }),
        svgContainerToPngBase64(f2GraphRef.current, {
          scale: 2, // 1)Low resolution, 2)High Resolution
          inlineImages: true,
        }),
      ]);

      if (!f1Graph || !f2Graph) {
        showMuiSnack('Graph capture failed. Please recalculate and try again.', 'warning');
        return;
      }

      const payload = {
        ...calculationInput,
        f1Graph,
        f2Graph,
      };

      const res = await fetch('/api/v1/pdf/stroud', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-stroud-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      // optionally show a snackbar
    } finally {
      setIsExporting(false);
    }
  };

  //---------------------------------------------//
  // RETURN INPUT AND OUTPUT JSX
  return (
    <>
      <SeoHelmet
        title="GeoCalc | cᵤ–mᵥ & SPT–PI — Stroud (1974, 1975)"
        description="Estimate undrained shear strength (cᵤ) and compressibility (mᵥ) of clays from PI and SPT N₆₀ using Stroud (1974, 1975) correlations."
        url="https://www.geocalc.app/calculations/stroud"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'cᵤ–mᵥ & SPT–PI Relationships', url: 'https://www.geocalc.app/calculations/stroud' },
        ]}
      />

      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            label="Show my calculations"
            onClick={handleSidebarDisplay}
          />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user.role} />

        <div className="right-calculation">
          <div className="stressIncreaseContainer">
            <h1>
              c<sub>u</sub> - m<sub>v</sub> Vs SPT - PI Relationships | Stroud 1974, 1975
            </h1>
            <div className="stressIncreaseInputContainer">
              <TextField
                style={{ minWidth: 80, maxWidth: 120 }}
                size="small"
                label="Name"
                variant="outlined"
                inputProps={{
                  maxLength: `${MAX_NAME_LENGTH}`,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={name}
                onChange={handleChangeName}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                style={{ minWidth: 80, maxWidth: 120 }}
                size="small"
                label="PI"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          <>
                            <div>Plasticity index of clay (%).</div>
                            <br />
                            <div>
                              For f<sub>1</sub> &rarr; valid PI values range between 15-64.{' '}
                            </div>
                            <div>
                              For f<sub>2</sub> &rarr; valid PI values range between 12-65.{' '}
                            </div>
                            <br />
                            <div>If you use PI values out of those ranges, results may be inaccurate!</div>
                          </>
                        }
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={pi}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Plasticity Index', setPi)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                size="small"
                style={{ minWidth: 80, maxWidth: 120 }}
                label={
                  <span key={'n60'} className="subscriptMargin">
                    N<sub>(60)</sub>
                  </span>
                }
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="SPT value for clay" arrow placement="bottom" TransitionComponent={Zoom}>
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={spt}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'SPT N', setSpt)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
            </div>
            <div className="buttonContainer stressIncreaseSaveCalculateButtonContainer">
              <CalculateButton
                calculationCountReached={calculationCountReached}
                calculate={calculate}
                calculationInput={calculationInput}
                URL={URL}
                calculateButtonDisabled={calculateButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
              <Button
                variant="contained"
                startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                onClick={handleExportPdf}
                disabled={!showOutput || isExporting}
                style={{ width: '200px' }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={18} style={{ marginRight: 8 }} />
                    Creating PDF…
                  </>
                ) : (
                  'Create PDF'
                )}
              </Button>
              <SaveButton
                saveCountReached={saveCountReached}
                checkCalculationNameforSaving={checkCalculationNameforSaving}
                calculationInput={calculationInput}
                URL={URL}
                saveButtonDisabled={saveButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
            </div>
            {isLoadingLoad && <CircularProgress size={30} />}
            {showOutput && <StroudResult result={result} />}
            {showOutput && <StroudPdfCaptureGraphs result={result} f1GraphRef={f1GraphRef} f2GraphRef={f2GraphRef} />}
            {isLoadingCalculate && (
              <div className="centerLoading">
                <CircularProgress />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

const StroudWithProvider = () => {
  return (
    <CalculationContextProvider>
      <Stroud />
    </CalculationContextProvider>
  );
};
export default StroudWithProvider;
